/**
 * LockedFeature — shown in place of a page whose feature isn't in the user's plan (see ProtectedRoute),
 * and LockBadge — the small 🔒 put on buttons / nav items for locked features.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@context/ThemeContext';
import { useEntitlements } from '@context/EntitlementContext';
import { FEATURE_BY_KEY } from '@/config/featureRegistry';

export const LockedFeature: React.FC<{ feature: string }> = ({ feature }) => {
  const navigate = useNavigate();
  const { themeConfig: tc } = useTheme();
  const { entitlements, isComingSoon } = useEntitlements();
  const label = FEATURE_BY_KEY[feature]?.label || 'This feature';
  if (isComingSoon(feature)) {
    return (
      <div className="flex items-center justify-center py-20 px-4">
        <div className={`max-w-lg w-full text-center rounded-2xl border p-8 ${tc.layout.card} ${tc.layout.cardBorder}`}>
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center text-3xl">✨</div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-violet-400 mb-2">Coming soon</p>
          <h1 className={`text-2xl font-bold mb-2 ${tc.layout.text}`}>{label} is almost here</h1>
          <p className={`mb-6 ${tc.layout.textSecondary}`}>It will switch on automatically for your plan the day it launches.</p>
          <button onClick={() => navigate('/dashboard')} className={`px-6 py-3 rounded-xl border font-semibold ${tc.layout.cardBorder} ${tc.layout.text}`}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }
  const hint = entitlements?.upgrade_hints?.[feature];
  const isSchoolOnly = hint?.plan_code === 'school';

  return (
    <div className="flex items-center justify-center py-20 px-4">
      <div className={`max-w-lg w-full text-center rounded-2xl border p-8 ${tc.layout.card} ${tc.layout.cardBorder}`}>
        <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-3xl">🔒</div>
        <h1 className={`text-2xl font-bold mb-2 ${tc.layout.text}`}>
          {label} {hint ? <>is part of <span className={tc.accent.primaryText}>{hint.plan_name}</span></> : 'isn’t in your plan'}
        </h1>
        <p className={`mb-6 ${tc.layout.textSecondary}`}>
          {isSchoolOnly
            ? 'This is included with a School account. Ask your school admin, or book a demo for your institution.'
            : `You're on ${entitlements?.plan_name || 'the Free plan'}. Upgrade to use ${label.toLowerCase()} and everything else in ${hint?.plan_name || 'a higher plan'}.`}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate('/pricing')} className={`px-6 py-3 rounded-xl text-white font-semibold ${tc.accent.primary} ${tc.accent.primaryHover}`}>
            {isSchoolOnly ? 'See school plans' : 'See plans & upgrade'}
          </button>
          <button onClick={() => navigate('/dashboard')} className={`px-6 py-3 rounded-xl border font-semibold ${tc.layout.cardBorder} ${tc.layout.text}`}>
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

/** Tiny lock marker for buttons / nav items whose feature is locked. Renders nothing when unlocked. */
export const LockBadge: React.FC<{ feature: string; className?: string }> = ({ feature, className = '' }) => {
  const { has, isComingSoon } = useEntitlements();
  if (isComingSoon(feature)) return <SoonBadge className={className} />;
  if (has(feature)) return null;
  return (
    <span
      aria-label="Locked — upgrade to use"
      title="Upgrade to unlock"
      className={`inline-flex items-center justify-center text-[10px] leading-none ${className}`}
    >
      🔒
    </span>
  );
};

/** "Soon" pill for announced-but-not-launched features (buttons, nav items, pricing). */
export const SoonBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    title="Coming soon"
    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-violet-500/20 text-violet-300 ${className}`}
  >
    Soon
  </span>
);

export default LockedFeature;
